import React, { ReactNode } from "react";
import ObservableComponent from "./ObservableComponent";
import * as ui from "../native/index";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
interface TextViewProps extends BaseUIProps {
  data: string;
  style?: ui.TextStyle;
  strutStyle?: ui.StrutStyle;
  textAlign?: ui.TextAlign;
  textDirection?: ui.TextDirection;
  locale?: ui.Locale;
  softWrap?: boolean;
  textScaleFactor?: number;
  maxLines?: number;
  semanticsLabel?: string;
  textWidthBasis?: ui.TextWidthBasis;
  textHeightBehavior?: ui.TextHeightBehavior;
  overflow?: ui.TextOverflow;
}

class _TextView extends ObservableComponent<TextViewProps> {
  private parseFormattedText(text: string, baseStyle?: ui.TextStyle): { text: string; formattedSpans: { text: string; style: ui.TextStyle; url?: string }[] } {
    if (!text) {
      return { text, formattedSpans: [] };
    }

    const spans: { text: string; style: ui.TextStyle; url?: string }[] = [];
    let currentPosition = 0;

    const patterns = [
      // Bold + Italic
      {
        regex: /\*\*\*(.+?)\*\*\*/,
        getStyle: () => new ui.TextStyle({ 
          fontWeight: ui.FontWeight.bold,
          fontStyle: ui.FontStyle.italic,
          inherit: true,
          ...baseStyle
        })
      },
      // Bold Link
      {
        regex: /\*\*\[(.+?)\]\((.+?)\)\*\*/,
        getStyle: () => new ui.TextStyle({ 
          fontWeight: ui.FontWeight.bold,
          color: baseStyle?.color || new ui.Color(0xff4287f5),
          decoration: ui.TextDecoration.underline,
          inherit: true,
          ...baseStyle,          
        }),
        isLink: true,
      },
      // Bold
      {
        regex: /\*\*(.+?)\*\*|__(.+?)__/,
        getStyle: () => new ui.TextStyle({ 
          fontWeight: ui.FontWeight.bold,
          inherit: true,
          ...baseStyle
        })
      },
      // Italic
      {
        regex: /\*(.+?)\*|_(.+?)_/,
        getStyle: () => new ui.TextStyle({ 
          fontStyle: ui.FontStyle.italic,
          inherit: true,
          ...baseStyle
        })
      },
      // Code
      {
        regex: /`(.+?)`/,
        getStyle: () => new ui.TextStyle({ 
          fontFamily: baseStyle?.fontFamily || "monospace",
          backgroundColor: baseStyle?.backgroundColor || new ui.Color(0xffF0F0F0),
          inherit: true,
          ...baseStyle
        })
      },
      // Link
      {
        regex: /\[(.+?)\]\((.+?)\)/,
        getStyle: () => new ui.TextStyle({ 
          color: baseStyle?.color || new ui.Color(0xff4287f5),
          decoration: ui.TextDecoration.underline,
          inherit: true,
          ...baseStyle
        }),
        isLink: true,
      }
    ];

    while (currentPosition < text.length) {
      const remaining = text.substring(currentPosition);
      let match: RegExpExecArray | null = null;
      let matchedPattern: typeof patterns[0] | null = null;
      let earliestIndex = text.length;

      // Find the earliest match
      for (const pattern of patterns) {
        const currentMatch = pattern.regex.exec(remaining);
        if (currentMatch && currentMatch.index < earliestIndex) {
          match = currentMatch;
          matchedPattern = pattern;
          earliestIndex = currentMatch.index;
        }
      }

      if (match && matchedPattern) {
        // Add normal text before match
        if (earliestIndex > 0) {
          spans.push({
            text: remaining.substring(0, earliestIndex),
            style: baseStyle
          });
        }

        // Add formatted text
        if (matchedPattern.isLink) {
          spans.push({
            text: match[1],  // Link text
            style: matchedPattern.getStyle(),
            url: match[2]    // URL from the match
          });
        } else {
          // Add formatted text
          spans.push({
            text: match[1],
            style: matchedPattern.getStyle()
          });
        }

        currentPosition += earliestIndex + match[0].length;
      } else {
        // Add remaining text
        spans.push({
          text: remaining,
          style: baseStyle
        });
        break;
      }
    }

    return { text, formattedSpans: spans };
  }
  render(): ReactNode {
    const { text, formattedSpans } = this.parseFormattedText(this.props.data, this.props.style);
    return ui.Text({
      data: text,
      formattedSpans: formattedSpans,
      style: this.props.style,
      strutStyle: this.props.strutStyle,
      textDirection: this.props.textDirection,
      textAlign: this.props.textAlign,
      locale: this.props.locale,
      softWrap: this.props.softWrap,
      textScaleFactor: this.props.textScaleFactor,
      maxLines: this.props.maxLines,
      semanticsLabel: this.props.semanticsLabel,
      textHeightBehavior: this.props.textHeightBehavior,
      overflow: this.props.overflow,
      textWidthBasis: this.props.textWidthBasis,
      className: this.props.className,
      ...copyBaseUIProps(this.props),
    });
  }
}

export default function TextView(props: TextViewProps) {
  return React.createElement(_TextView, props);
}
